import { completeSession } from "@Containers/shared/helpers";
import { AppContext } from "@Context/common/general/context";
import PlaySessionEventActionTypes from "@Enums/PlaySessionEventActionTypes";
import type { PlaySessionEvent } from "@Types/unity-events";
import type { RawSessionResultsData } from "@hooks/game/activity/session/use-session-results.types";
import { isNumber } from "lodash-es";
import { useContext, useEffect, useRef, useState } from "react";

interface UseCompletePlaySessionOptions {
  delayMs?: number;
  matches?: (event: PlaySessionEvent, playSessionId: number) => boolean;
  onSuccess: (results: RawSessionResultsData, playSessionId: number, event: PlaySessionEvent) => void;
  onError?: (error: unknown, playSessionId: number, event: PlaySessionEvent) => void;
}

const useCompletePlaySession = ({ delayMs = 0, matches, onSuccess, onError }: UseCompletePlaySessionOptions) => {
  const { state, dispatch } = useContext(AppContext);
  const [isCompletingSession, setIsCompletingSession] = useState(false);
  const optionsRef = useRef({ delayMs, matches, onSuccess, onError });
  const dispatchRef = useRef(dispatch);
  const playSessionEventRef = useRef(state.app.events.playSessionEvent);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightSessionIdRef = useRef<number | null>(null);
  const handledSessionIdRef = useRef<number | null>(null);
  optionsRef.current = { delayMs, matches, onSuccess, onError };
  dispatchRef.current = dispatch;

  useEffect(() => {
    let mounted = true;

    const onPlaySessionEventReceived = (event: PlaySessionEvent) => {
      const playSessionId = event.play_session_id ?? event.id;
      const options = optionsRef.current;

      if (
        event.action !== PlaySessionEventActionTypes.COMPLETE ||
        !isNumber(playSessionId) ||
        options.matches?.(event, playSessionId) === false ||
        inFlightSessionIdRef.current !== null ||
        handledSessionIdRef.current === playSessionId
      ) {
        return;
      }

      inFlightSessionIdRef.current = playSessionId;
      setIsCompletingSession(true);

      const complete = () => {
        timerRef.current = null;
        completeSession(
          dispatchRef.current,
          playSessionId,
          results => {
            if (!mounted) return;
            inFlightSessionIdRef.current = null;
            handledSessionIdRef.current = playSessionId;
            setIsCompletingSession(false);
            optionsRef.current.onSuccess(results, playSessionId, event);
          },
          error => {
            if (!mounted) return;
            inFlightSessionIdRef.current = null;
            setIsCompletingSession(false);
            optionsRef.current.onError?.(error, playSessionId, event);
          }
        );
      };

      if (options.delayMs > 0) {
        timerRef.current = setTimeout(complete, options.delayMs);
      } else {
        complete();
      }
    };

    const playSessionEvent = playSessionEventRef.current;
    playSessionEvent.uniq(onPlaySessionEventReceived);
    return () => {
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      playSessionEvent.remove(onPlaySessionEventReceived);
    };
  }, []);

  return { isCompletingSession };
};

export default useCompletePlaySession;
